import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { memberApi } from "../lib/memberApi";
import { api } from "../lib/api";
import MemberLayout from "../components/MemberLayout";
import { getAvatarSrc } from "../lib/avatar";

import {
  UserCog,
  HandHeart,
  AlertCircle,
  CheckCircle2,
  Clock,
  Megaphone,
  Users,
  CreditCard,
  Info,
} from "lucide-react";

export default function MemberDashboard() {
  const nav = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [welfare, setWelfare] = useState([]);
  const [members, setMembers] = useState([]);
  const [showMissing, setShowMissing] = useState(false);

  // =========================
  // AUTO LOGOUT
  // =========================
  useEffect(() => {
    let timer;

    const logoutNow = () => {
      localStorage.removeItem("ospf_member_token");
      nav("/member/login", { replace: true });
    };

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(logoutNow, 10 * 60 * 1000);
    };

    ["mousemove", "click", "keydown", "scroll"].forEach((e) =>
      window.addEventListener(e, resetTimer),
    );

    resetTimer();

    return () => {
      clearTimeout(timer);
      ["mousemove", "click", "keydown", "scroll"].forEach((e) =>
        window.removeEventListener(e, resetTimer),
      );
    };
  }, [nav]);

  // =========================
  // LOAD USER
  // =========================
  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await memberApi.get("/members/me");
        // Backend returns { member: {...} } or the object directly
        setUser(data.member || data);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          // Genuine auth failure — clear token and go to login
          localStorage.removeItem("ospf_member_token");
          nav("/member/login", { replace: true });
        }
        // On 5xx / timeout (Render cold start) keep loading=false so the
        // page can show a retry UI rather than silently logging out.
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [nav]);

  // =========================
  // LOAD WELFARE
  // =========================
  useEffect(() => {
    api.get("/member/welfare").then(({ data }) => setWelfare(data));
  }, []);

  // =========================
  // LOAD MEMBERS (for suggestions)
  // =========================
  useEffect(() => {
    api
      .get("/members/directory")
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : data?.items || [];
        setMembers(list.slice(0, 10)); // lightweight
      })
      .catch(() => setMembers([]));
  }, []);

  // =========================
  // PROFILE COMPLETENESS
  // =========================
  const completionItems = useMemo(() => {
    if (!user) return 0;
    return [
      { label: "Profile photo", value: user.avatar_path },
      { label: "Full name", value: user.name },
      { label: "Email", value: user.email },
      { label: "Phone number", value: user.phone },
      { label: "Country", value: user.country_name || user.country_code },
      { label: "City", value: user.city },
      { label: "Profession", value: user.profession },
      { label: "Business / firm name", value: user.business_name },
      { label: "Role / position", value: user.role },
      { label: "Experience", value: user.experience },
      { label: "Business details", value: user.business_details },
      { label: "Payment status", value: user.payment_status },
      { label: "Membership status", value: user.membership_status },
    ];
  }, [user]);

  const missingItems = useMemo(
    () =>
      Array.isArray(completionItems)
        ? completionItems
          .filter((item) => !String(item.value || "").trim())
          .map((item) => item.label)
        : [],
    [completionItems],
  );

  const completeness = useMemo(() => {
    if (!Array.isArray(completionItems) || completionItems.length === 0) return 0;
    return Math.round(
      ((completionItems.length - missingItems.length) / completionItems.length) *
      100,
    );
  }, [completionItems, missingItems]);

  // =========================
  // SUGGESTED CONNECTIONS
  // =========================
  const suggestions = useMemo(() => {
    if (!user) return [];

    return members.filter((m) => m.id !== user.id).slice(0, 4);
  }, [members, user]);

  const stats = {
    pending: welfare.filter((w) => w.status === "pending").length,
    approved: welfare.filter((w) => w.status === "approved").length,
    rejected: welfare.filter((w) => w.status === "rejected").length,
  };

  if (loading) {
    return (
      <MemberLayout>
        <div className="py-20 text-center">Loading...</div>
      </MemberLayout>
    );
  }

  if (!user) return null;

  const isApproved = user.membership_status === "approved";
  const isPaymentDue =
    user.membership_status === "draft" ||
    user.payment_status === "awaiting_payment";

  return (
    <MemberLayout>
      <div className="max-w-5xl mx-auto">
        {/* 🔥 STATUS ALERT */}
        {!isApproved && (
          <div className="mb-6 p-4 rounded-2xl bg-amber-50 border border-amber-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              {isPaymentDue ? (
                <CreditCard className="w-5 h-5 text-amber-600" />
              ) : (
                <AlertCircle className="w-5 h-5 text-amber-600" />
              )}
              <div>
                <div className="font-medium text-amber-700">
                  {isPaymentDue ? "Payment pending" : "Membership pending approval"}
                </div>
                <div className="text-sm text-amber-600">
                  {isPaymentDue
                    ? "Your form is saved. Upload payment proof to send it for admin approval."
                    : "Admin will review your payment proof and approve the membership."}
                </div>
              </div>
            </div>
            {isPaymentDue ? (
              <Link
                to={`/checkout/${user.id}`}
                className="btn-primary w-full sm:w-auto justify-center"
              >
                Continue payment
              </Link>
            ) : null}
          </div>
        )}

        {/* 🔝 HEADER */}
        <div className="flex flex-col md:flex-row md:justify-between items-start gap-6 mb-10">
          <div className="flex gap-5">
            {getAvatarSrc(user) ? (
              <img
                src={getAvatarSrc(user)}
                alt="avatar"
                className="w-20 h-20 rounded-2xl object-contain bg-white"
              />
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-brand/10 text-brand flex items-center justify-center text-2xl font-semibold">
                {user.name
                  ?.split(" ")
                  ?.map((x) => x[0])
                  ?.join("")
                  ?.toUpperCase()}
              </div>
            )}

            <div>
              <h1 className="text-3xl font-light">{user.name}</h1>
              <div className="text-sm mt-1">
                {user.profession || "Member"} · {user.city || "India"}
              </div>

              {/* 🔥 PROFILE COMPLETENESS */}
              <div className="mt-3">
                <div className="flex items-center gap-2 text-xs text-ink-soft mb-1">
                  <span>Profile completeness: {completeness}%</span>
                  <button
                    type="button"
                    onClick={() => setShowMissing((value) => !value)}
                    className="w-5 h-5 rounded-full border border-black/10 bg-white text-ink-muted hover:text-ink inline-flex items-center justify-center"
                    aria-label="Show missing profile details"
                  >
                    <Info className="w-3 h-3" />
                  </button>
                </div>
                <div className="w-48 h-2 bg-gray-200 rounded-full">
                  <div
                    className="h-2 bg-green-600 rounded-full"
                    style={{ width: `${completeness}%` }}
                  />
                </div>
                {showMissing ? (
                  <div className="mt-3 max-w-sm rounded-2xl border border-black/10 bg-white p-3 text-xs text-ink-soft shadow-sm">
                    {missingItems.length ? (
                      <>
                        <div className="font-medium text-ink mb-2">
                          Still missing
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {missingItems.map((item) => (
                            <span
                              key={item}
                              className="rounded-full bg-cream-deep px-2.5 py-1"
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="font-medium text-green-700">
                        Profile is complete.
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* ACTIONS */}
          <div className="flex gap-2 flex-wrap">
            <Link
              to="/member/profile"
              className="btn-secondary w-full sm:w-auto justify-center"
            >
              <UserCog className="w-4 h-4" /> Profile
            </Link>

            <Link
              to="/activities/feed"
              className="btn-secondary w-full sm:w-auto justify-center"
            >
              <Megaphone className="w-4 h-4" /> Activity
            </Link>

            <Link
              to="/member/welfare"
              className="btn-primary w-full sm:w-auto justify-center"
            >
              <HandHeart className="w-4 h-4" /> Welfare
            </Link>
          </div>
        </div>



        {/* 🔥 ENGAGEMENT */}
        <div className="mt-8 p-5 rounded-2xl bg-brand/5 border border-brand/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="font-medium">Explore community</div>
            <div className="text-sm text-ink-soft">
              Discover members and activities
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Link
              to="/directory"
              className="btn-secondary text-sm w-full sm:w-auto justify-center"
            >
              Directory
            </Link>
            <Link
              to="/activities/feed"
              className="btn-primary text-sm w-full sm:w-auto justify-center"
            >
              Activity
            </Link>
          </div>
        </div>

        {/* 🔥 SUGGESTED CONNECTIONS */}
        {suggestions.length > 0 && (
          <div className="mt-10">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5" />
              <h2 className="text-lg font-semibold">Suggested Connections</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {suggestions.map((m) => (
                <div key={m.id} className="card-premium text-center">
                  <div className="w-12 h-12 mx-auto rounded-full bg-brand/10 flex items-center justify-center text-sm font-semibold">
                    {m.name?.slice(0, 2).toUpperCase()}
                  </div>

                  <div className="mt-2 text-sm font-medium">{m.name}</div>

                  <div className="text-xs text-ink-soft">{m.profession}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </MemberLayout>
  );
}

function Card({ label, value, icon: Icon }) {
  return (
    <div className="card-premium">
      <Icon className="w-5 h-5 mb-2" />
      <div className="text-sm">{label}</div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}
